import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { useLang } from '@/context/LangContext';
import { useTeams } from '@/context/TeamContext';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { MessageSquare, Plus, Send, Trash2, Users } from 'lucide-react';
import { AttachPlayPicker, PlayAttachmentBadge } from '@/components/PlayAttachment';

function fmtTime(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

export default function Groups() {
  const { t } = useLang();
  const { user } = useAuth();
  const { teams, activeTeam, activeTeamId } = useTeams();
  const isCoach = user?.role === 'coach' || user?.role === 'admin';
  const [scopeTeamId, setScopeTeamId] = useState(activeTeamId || null);
  const [groups, setGroups] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [attachedPlay, setAttachedPlay] = useState(null);
  const [newName, setNewName] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => { if (activeTeamId && !scopeTeamId) setScopeTeamId(activeTeamId); }, [activeTeamId, scopeTeamId]);

  const loadGroups = async () => {
    if (!scopeTeamId) return;
    try {
      const r = await api.get(`/groups?team_id=${scopeTeamId}`);
      setGroups(r.data.groups || []);
      const first = r.data.groups?.[0];
      // Reset selection when changing team
      if (first && !(r.data.groups || []).some(g => g.id === selectedId)) {
        setSelectedId(first.id);
      } else if (!first) {
        setSelectedId(null);
        setMessages([]);
      }
    } catch {}
  };

  const loadMessages = async (gid) => {
    if (!gid) return;
    try {
      const r = await api.get(`/groups/${gid}/messages`);
      setMessages(r.data.messages || []);
      setTimeout(() => scrollRef.current?.scrollTo(0, 99999), 50);
    } catch {}
  };

  useEffect(() => { loadGroups(); }, [scopeTeamId]);
  useEffect(() => { loadMessages(selectedId); }, [selectedId]);

  // Poll messages every 5s while a group is open
  useEffect(() => {
    if (!selectedId) return;
    const id = setInterval(() => loadMessages(selectedId), 5000);
    return () => clearInterval(id);
  }, [selectedId]);

  const createGroup = async () => {
    if (!newName.trim() || !scopeTeamId) return;
    try {
      const r = await api.post('/groups', { name: newName.trim(), team_id: scopeTeamId });
      toast.success(t('group_created'));
      setNewName('');
      await loadGroups();
      setSelectedId(r.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const sendMessage = async () => {
    if (!text.trim() || !selectedId) return;
    const optimistic = {
      id: `tmp-${Date.now()}`,
      message: text,
      play_summary: attachedPlay,
      sender_name: user?.name,
      sender_id: user?.id,
      sender_role: user?.role,
      created_at: new Date().toISOString(),
    };
    setMessages(m => [...m, optimistic]);
    setText('');
    const playId = attachedPlay?.id;
    setAttachedPlay(null);
    try {
      await api.post(`/groups/${selectedId}/messages`, {
        message: optimistic.message,
        play_id: playId || undefined,
      });
      loadMessages(selectedId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const deleteGroup = async (id) => {
    if (!window.confirm(t('group_delete_confirm'))) return;
    try {
      await api.delete(`/groups/${id}`);
      toast.success(t('group_deleted'));
      if (selectedId === id) setSelectedId(null);
      loadGroups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <Layout>
      <BackToDashboard />
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('nav_groups')}</div>
        <h1 className="font-h uppercase text-4xl sm:text-5xl">{t('groups_h1')}</h1>
        {/* Team picker — ONLY for coaches. Players are restricted to the
            groups of their own coach's active team (privacy: a player must
            never see another team's groups). */}
        {isCoach && teams.length > 0 && (
          <div className="mt-3 inline-flex items-center gap-2 flex-wrap" data-testid="groups-team-picker">
            <span className="text-xs font-mono uppercase tracking-widest text-gray-400">{t('team_choose') || 'Equipo'}:</span>
            {teams.map(tm => (
              <button
                key={tm.id}
                onClick={() => setScopeTeamId(tm.id)}
                data-testid={`groups-team-${tm.id}`}
                className={`px-3 py-1 rounded-full text-xs font-h uppercase tracking-wider border ${
                  scopeTeamId === tm.id ? 'bg-gold text-navy border-gold' : 'border-gold/30 text-gold hover:bg-gold/10'
                }`}
              >
                {tm.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-5">
        {/* Group list */}
        <div className="bg-card-steel border border-gold/30 rounded-lg p-4">
          {isCoach && (
            <div className="flex gap-2 mb-4">
              <input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') createGroup(); }}
                placeholder={t('group_name_placeholder')}
                data-testid="group-create-name"
                className="flex-1 bg-navy-deep border border-steel rounded px-2 py-1.5 text-sm text-white"
                maxLength={80}
              />
              <button onClick={createGroup} data-testid="group-create-btn" className="px-3 py-1.5 rounded bg-gold text-navy hover:bg-gold-light"><Plus className="w-4 h-4" /></button>
            </div>
          )}
          {groups.length === 0 && <p className="text-xs text-gray-400 text-center py-4">{t('groups_empty')}</p>}
          {groups.map(g => (
            <div key={g.id} className={`group flex items-center justify-between rounded mb-1 ${selectedId === g.id ? 'bg-gold/15' : 'hover:bg-gold/5'}`}>
              <button onClick={() => setSelectedId(g.id)} data-testid={`group-item-${g.id}`} className={`flex-1 text-left text-sm font-h uppercase tracking-wider px-3 py-2 ${selectedId === g.id ? 'text-gold' : 'text-gray-200'}`}>
                <Users className="w-3.5 h-3.5 inline mr-2" />{g.name}
              </button>
              {isCoach && (
                <button onClick={() => deleteGroup(g.id)} className="p-2 text-alert hover:bg-alert/10 opacity-0 group-hover:opacity-100">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Message thread */}
        <div className="bg-card-steel border border-gold/30 rounded-lg flex flex-col" style={{ minHeight: '500px', maxHeight: 'calc(100vh - 240px)' }}>
          {!selectedId ? (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <MessageSquare className="w-12 h-12 opacity-40 mr-3" /> {t('group_select')}
            </div>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2">
                {messages.map(m => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-lg px-3 py-2 ${mine ? 'bg-gold/15 border border-gold/40' : 'bg-navy-deep border border-steel'}`}>
                        {!mine && (
                          <div className="text-[10px] font-mono uppercase tracking-widest text-gold mb-0.5">
                            {m.sender_name} · {m.sender_role}
                          </div>
                        )}
                        <div className="text-sm whitespace-pre-wrap break-words">{m.message}</div>
                        {m.play_summary && <PlayAttachmentBadge summary={m.play_summary} />}
                        <div className="text-[10px] text-gray-500 mt-1 text-right font-mono">{fmtTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && <p className="text-center text-xs text-gray-500 py-8">{t('group_no_messages')}</p>}
              </div>
              <form
                className="border-t border-gold/20 p-3 flex gap-2 items-center"
                onSubmit={e => { e.preventDefault(); sendMessage(); }}
              >
                <AttachPlayPicker selected={attachedPlay} onSelect={setAttachedPlay} />
                <input
                  value={text}
                  onChange={e => setText(e.target.value)}
                  placeholder={t('group_message_placeholder')}
                  data-testid="group-msg-input"
                  className="flex-1 bg-navy-deep border border-steel rounded px-3 py-2 text-sm text-white"
                  maxLength={2000}
                />
                <button type="submit" data-testid="group-msg-send" disabled={!text.trim()} className="px-4 py-2 rounded bg-gold text-navy hover:bg-gold-light disabled:opacity-50">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
